import type { Scene } from "@babylonjs/core/scene";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreatePlane } from "@babylonjs/core/Meshes/Builders/planeBuilder";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";

/** The PixelLab sheet (tools/pixellab/pack-ghost.py): row 0 idle, row 1 glide, 8 frames of 128 x 128. */
const SHEET = "/proto2d/ghost/sheet.png";
const CELL = 128;
const FRAMES = 8;
const FPS = { idle: 8, walk: 12 } as const;
/** Metres per pixel: the champions' scale, so the ghost stands as tall as a short fighter. */
const METRES_PER_PIXEL = (1.8 * 1.3) / 100;
/** Transparent rows under the lowest point of the hem, and how high the hem floats. */
const BELOW_HEM = (CELL - 97) * METRES_PER_PIXEL;
const FLOAT = 0.14;
/** Faster than this (m/s) on screen, the ghost glides. */
const GLIDING = 0.6;
/**
 * The glide clip is not a loop: it starts upright and ends leaning into the
 * glide. Its first frames play once, as the ghost sets off; then the leaning
 * frames go back and forth, so the cycle never jumps.
 */
const SET_OFF = 3;
const LEANING = [3, 4, 5, 6, 7, 6, 5, 4] as const;
/** Gliding, the ghost rises a little and bobs, so it floats rather than drags. */
const GLIDE_LIFT = 0.2;
const GLIDE_BOB = 0.05;

const right = new Vector3();

/**
 * A player who has not picked a champion yet (D49): the PixelLab sheet ghost,
 * floating idle or gliding, tinted in the player's colour (the sheet is white,
 * the eyes stay black), a little see-through. Faces where it goes on screen.
 */
export class GhostView {
  private readonly scene: Scene;
  private readonly mesh: Mesh;
  private readonly material: StandardMaterial;
  private readonly texture: Texture;
  private time = Math.random() * 10;
  private lastX = Number.NaN;
  private lastZ = 0;
  private speed = 0;
  private flipped = false;
  private shown = "";
  /** How long it has been gliding (0 when still), and how high it is lifted now. */
  private glide = 0;
  private lift = 0;

  constructor(scene: Scene, color: Color3) {
    this.scene = scene;
    this.texture = new Texture(SHEET, scene, true, true, Texture.NEAREST_SAMPLINGMODE);
    this.texture.hasAlpha = true;
    this.texture.wrapU = Texture.CLAMP_ADDRESSMODE;
    this.texture.wrapV = Texture.CLAMP_ADDRESSMODE;
    const size = CELL * METRES_PER_PIXEL;
    this.mesh = CreatePlane("ghost", { size }, scene);
    this.mesh.bakeTransformIntoVertices(Matrix.Translation(0, size / 2, 0));
    this.mesh.billboardMode = Mesh.BILLBOARDMODE_Y;
    this.mesh.isPickable = false;
    this.material = new StandardMaterial("ghost", scene);
    this.material.diffuseTexture = this.texture;
    this.material.useAlphaFromDiffuseTexture = true;
    this.material.disableLighting = true;
    // Unlit, so the emissive colour is the light: the sheet's white times a pale version of the player's.
    this.material.diffuseColor = Color3.Black();
    this.material.emissiveColor = color.add(Color3.White().subtract(color).scale(0.5));
    this.material.specularColor = Color3.Black();
    this.material.backFaceCulling = false;
    this.material.alpha = 0.88;
    this.mesh.material = this.material;
  }

  update(x: number, y: number, z: number, fx: number, fz: number, dt: number): void {
    this.time += dt;
    const moved = Number.isNaN(this.lastX) ? 0 : Math.hypot(x - this.lastX, z - this.lastZ) / Math.max(dt, 1e-3);
    this.lastX = x;
    this.lastZ = z;
    this.speed += (Math.min(moved, 20) - this.speed) * (1 - Math.exp(-dt * 10));
    // The sheet faces right on screen: mirrored when going left.
    this.scene.activeCamera?.getDirectionToRef(Vector3.Right(), right);
    const onRight = fx * right.x + fz * right.z;
    if (Math.abs(onRight) > 0.15) this.flipped = onRight < 0;
    const gliding = this.speed > GLIDING;
    this.glide = gliding ? this.glide + dt : 0;
    if (gliding) {
      const step = Math.floor(this.glide * FPS.walk);
      this.show(1, step < SET_OFF ? step : LEANING[(step - SET_OFF) % LEANING.length]!);
    } else {
      this.show(0, Math.floor(this.time * FPS.idle) % FRAMES);
    }
    this.lift += ((gliding ? GLIDE_LIFT : 0) - this.lift) * (1 - Math.exp(-dt * 6));
    const bob = gliding ? Math.sin(this.glide * 7) * GLIDE_BOB : 0;
    this.mesh.position.set(x, y + FLOAT + this.lift + bob - BELOW_HEM, z);
  }

  setEnabled(on: boolean): void {
    this.mesh.setEnabled(on);
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
    this.texture.dispose();
  }

  private show(row: number, frame: number): void {
    const key = `${row}:${frame}:${this.flipped}`;
    if (key === this.shown) return;
    this.shown = key;
    const w = 1 / FRAMES;
    this.texture.uScale = this.flipped ? -w : w;
    this.texture.uOffset = (frame + (this.flipped ? 1 : 0)) * w;
    this.texture.vScale = 0.5;
    this.texture.vOffset = row === 0 ? 0.5 : 0;
  }
}
